import { DefaultButton, Dialog, DialogFooter, MessageBar, MessageBarType, PrimaryButton, Spinner, Stack, Text } from "@fluentui/react"
import { useEffect, useState } from "react"
import { medicalRequest, QuestionnaireCampaign, QuestionnairePreflight } from "../services/medicalQuestionnaireService"

export function MedicalQuestionnaireDispatch({ accountId, censusVersion }: { accountId: string; censusVersion: boolean }) {
	const [preflight, setPreflight] = useState<QuestionnairePreflight>()
	const [loading, setLoading] = useState(true)
	const [sending, setSending] = useState(false)
	const [confirm, setConfirm] = useState(false)
	const [error, setError] = useState<string>()
	const [refresh, setRefresh] = useState(0)

	useEffect(() => {
		const controller = new AbortController()
		setLoading(true)
		setPreflight(undefined)
		setError(undefined)
		setConfirm(false)
		async function load() {
			try {
				const result = await medicalRequest<QuestionnairePreflight>(`groups/${accountId}/preflight`, undefined, true, controller.signal)
				if (!controller.signal.aborted) setPreflight(result)
			} catch (failure) {
				if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "Unable to check questionnaire eligibility.")
			} finally {
				if (!controller.signal.aborted) setLoading(false)
			}
		}
		void load()
		return () => controller.abort()
	}, [accountId, censusVersion, refresh])

	useEffect(() => {
		if (!preflight?.campaign?.queued || sending) return
		const timer = window.setTimeout(() => setRefresh(value => value + 1), 10000)
		return () => window.clearTimeout(timer)
	}, [preflight, sending])

	async function send() {
		setSending(true)
		setError(undefined)
		try {
			const campaign = await medicalRequest<QuestionnaireCampaign>(`groups/${accountId}/send`, { fingerprint: preflight.fingerprint })
			setPreflight(current => ({ ...current, campaign }))
			setConfirm(false)
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : "The invitations could not be scheduled. Refresh before retrying.")
			setConfirm(false)
		} finally {
			setSending(false)
		}
	}

	const campaign = preflight?.campaign
	return (
		<Stack tokens={{ childrenGap: 12 }} styles={{ root: { border: "1px solid #d2dce5", borderRadius: 8, padding: 16, background: "#f7fafc" } }}>
			<Text variant="large" as="h3" styles={{ root: { margin: 0 } }}>
				Medical questionnaires
			</Text>
			<Text>Send a personal, email-verified onboarding link to each active, non-terminated employee. Household members will be included in the later questionnaire stage.</Text>
			{loading && <Spinner label="Checking every employee's email address..." />}
			{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			{preflight?.blockers.length > 0 && (
				<MessageBar messageBarType={MessageBarType.warning} isMultiline>
					Medical questionnaires cannot be sent until all eligible employees have a valid, unique email address. Correct the employee records below, then refresh eligibility.
					<ul>
						{preflight.blockers.map(employee => (
							<li key={employee.id}>
								{employee.name}: {employee.reason === "shared-email" ? "email address is shared with another employee" : "email address is missing or invalid"}
							</li>
						))}
					</ul>
				</MessageBar>
			)}
			{preflight && !preflight.employeeCount && <MessageBar>There are no active, non-terminated employees to invite.</MessageBar>}
			{preflight?.dispatchLimitExceeded && (
				<MessageBar messageBarType={MessageBarType.warning}>
					This release supports up to 400 employees per group. This census exceeds the atomic dispatch limit; contact ACMG for assistance.
				</MessageBar>
			)}
			{preflight?.censusChanged && (
				<MessageBar messageBarType={MessageBarType.warning}>
					The census differs from the existing onboarding campaign. Contact ACMG for review; replacement links will not be sent automatically.
				</MessageBar>
			)}
			{campaign && (
				<MessageBar messageBarType={campaign.failed ? MessageBarType.warning : MessageBarType.info} isMultiline>
					{campaign.total} invitations: {campaign.queued} queued/processing, {campaign.submittedToEmail} handed to the email flow, {campaign.failed} requiring staff review;{" "}
					{campaign.verified} emails verified; {campaign.registered || 0} signed in and started; {campaign.completed || 0} questionnaires completed. Handed to the email flow does not confirm
					inbox delivery. Reopening this group or clicking again will not create duplicate invitations.
					{campaign.failed > 0 && " ACMG staff must review failed/uncertain flow runs before any resend."}
				</MessageBar>
			)}
			<MessageBar>Sandbox questionnaires use sample privacy/consent wording. Use synthetic test data only; production medical collection remains disabled.</MessageBar>
			<Stack horizontal wrap tokens={{ childrenGap: 10 }}>
				<PrimaryButton
					text={sending ? "Scheduling invitations..." : "Send medical questionnaires"}
					disabled={loading || sending || !preflight?.canSend || !!campaign}
					onClick={() => setConfirm(true)}
				/>
				<DefaultButton text="Refresh eligibility and status" disabled={loading || sending} onClick={() => setRefresh(value => value + 1)} />
				<DefaultButton text="Review group questionnaires" href={`/medical-questionnaire/?accountId=${encodeURIComponent(accountId)}`} />
			</Stack>
			{confirm && (
				<Dialog
					hidden={false}
					onDismiss={() => !sending && setConfirm(false)}
					dialogContentProps={{
						title: "Send medical questionnaire invitations",
						subText: `Send one personal link to each of the ${preflight.employeeCount} eligible employees? No medical questions are enabled yet. Emails cannot be recalled after they are handed to the email flow.`
					}}
					modalProps={{ isBlocking: sending }}>
					<DialogFooter>
						<PrimaryButton text={sending ? "Scheduling..." : "Send invitations"} disabled={sending} onClick={send} />
						<DefaultButton text="Cancel" disabled={sending} onClick={() => setConfirm(false)} />
					</DialogFooter>
				</Dialog>
			)}
		</Stack>
	)
}
